import styles from "./styles.module.scss"
import { useMemo, useEffect, useRef } from "react"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import { SearchResultDetailsItineraryItem } from "@cruisen/contract"
import { CruiseDetailsCard } from "../../../../../components/cards/CruiseDetailsCard"
import { _t } from "@cruisen/i18n"
import { datesFormats, formatDate } from "@cruisen/react-shared"
import ChevronDownOutline from "~icons/eva/chevron-down-outline"
import { colors } from "@theme/colors"

class TripCardState {
  showAllItineraryItems: boolean
  dayElementsY: Record<number, number>
  gradientLineHeight: number

  constructor() {
    this.showAllItineraryItems = false
    this.dayElementsY = {}
    this.gradientLineHeight = 0

    makeAutoObservable(this)
  }

  toggleShowAllItineraryItems() {
    this.showAllItineraryItems = !this.showAllItineraryItems
  }

  resetDayElements() {
    this.dayElementsY = {}
  }

  setGradientLineHeight(height: number) {
    this.gradientLineHeight = height
  }

  setDayElementsPositions(positions: number[]) {
    this.dayElementsY = positions.reduce(
      (acc, y, index) => ({
        ...acc,
        [index + 1]: y,
      }),
      {},
    )
  }
}

const placeholderItems = Array.from({ length: 4 }, (_, i) => ({
  id: i + 1,
  day: i + 1,
  arriveDate: null,
  name: "",
}))

const calculateColor = (startColor: string, endColor: string, percentage: number) => {
  const startRGB = parseInt(startColor.slice(1), 16)
  const endRGB = parseInt(endColor.slice(1), 16)

  const startR = startRGB >> 16
  const startG = (startRGB >> 8) & 0xff
  const startB = startRGB & 0xff

  const endR = endRGB >> 16
  const endG = (endRGB >> 8) & 0xff
  const endB = endRGB & 0xff

  const r = Math.round((endR - startR) * percentage + startR)
  const g = Math.round((endG - startG) * percentage + startG)
  const b = Math.round((endB - startB) * percentage + startB)

  return `rgb(${r}, ${g}, ${b})`
}

export const TripCard = observer(function TripCard({
  itineraryItems,
  tripDatesInfo,
  isLoadingData,
}: {
  itineraryItems: SearchResultDetailsItineraryItem[]
  tripDatesInfo: string
  isLoadingData: boolean
}) {
  const store = useMemo(() => new TripCardState(), [])
  const itineraryContainerRef = useRef<HTMLDivElement>(null)
  const gradientLineRef = useRef<HTMLDivElement>(null)
  const dayElementsRef = useRef<(HTMLParagraphElement | null)[]>([])

  const items = isLoadingData || !itineraryItems.length ? placeholderItems : itineraryItems
  const areMoreItems = itineraryItems.length > 4
  const visibleItems = items.slice(0, store.showAllItineraryItems ? itineraryItems.length : 4)

  const shouldShowAllItems = store.showAllItineraryItems || !areMoreItems

  useEffect(() => {
    store.resetDayElements()
    const container = itineraryContainerRef.current
    const gradientLine = gradientLineRef.current
    if (!container || !gradientLine) return

    const containerRect = container.getBoundingClientRect()
    const positions = dayElementsRef.current
      .filter(Boolean)
      .map((element) => element!.getBoundingClientRect().y - containerRect.y)

    store.setDayElementsPositions(positions)
    store.setGradientLineHeight(gradientLine.clientHeight)
  }, [items.length, store.showAllItineraryItems])

  return (
    <CruiseDetailsCard title={_t("cruiseDetailsScreen.trip")} subtitle={tripDatesInfo}>
      <div className={styles.container}>
        <div className={styles.maskedViewContainer}>
          {Object.values(store.dayElementsY).map((positionY) => {
            if (positionY > store.gradientLineHeight) return null

            return (
              <div
                key={positionY}
                className={styles.gradientDot}
                style={{
                  top: positionY + 6,
                  backgroundColor: calculateColor(
                    colors.strokeBlueAccent,
                    colors.strokeGreenAccent,
                    positionY / store.gradientLineHeight,
                  ),
                }}
              />
            )
          })}
          <div className={styles.gradient} id="gradient-line" ref={gradientLineRef} />
          <div className={styles.maskedView}>
            {!shouldShowAllItems && (
              <div className={styles.maskedViewList}>
                <div className={styles.maskedViewDot} />
                <div className={styles.maskedViewDot} />
                <div className={styles.maskedViewDot} />
              </div>
            )}
          </div>
          {shouldShowAllItems && !isLoadingData && (
            <div className={styles.chevronDown}>
              <ChevronDownOutline className={styles.chevronDownIcon} />
            </div>
          )}
        </div>
        <div className={styles.itineraryItems} ref={itineraryContainerRef}>
          {visibleItems.map((item, index) => (
            <div className={styles.itineraryItemContainer} key={item.id}>
              <p
                className={styles.itineraryItemDay}
                ref={(el) => (dayElementsRef.current[index] = el)}
              >
                {_t("cruiseDetailsScreen.day", {
                  day: item.day,
                  date: item?.arriveDate
                    ? formatDate(
                        new Date(item.arriveDate).toISOString(),
                        datesFormats.shortenedMonthNameWithDay,
                      )
                    : "",
                })}
              </p>
              {isLoadingData ? (
                <div className={styles.itineraryItemLoading} />
              ) : (
                <p
                  className={styles.itineraryItemName}
                  data-testid={`cruise-itinerary-item-${index + 1}`}
                >
                  {item.name}
                </p>
              )}
            </div>
          ))}
          {!isLoadingData && !shouldShowAllItems && <div className={styles.showMoreGradient} />}
        </div>
      </div>
      {itineraryItems.length > 4 && !isLoadingData && (
        <button
          className={styles.showMoreButton}
          onClick={() => store.toggleShowAllItineraryItems()}
        >
          {store.showAllItineraryItems ? "Show less" : "Show more"}
        </button>
      )}
    </CruiseDetailsCard>
  )
})
